import type { Locale } from './game/types';

export interface PlayerSettings {
  masterVolume: number;
  musicVolume: number;
  effectsVolume: number;
  reducedMotion: boolean;
  fastAnimations: boolean;
  highContrast: boolean;
}

export interface PlayerState {
  version: 2;
  profile: { callsign: string; publicCode: string; avatar: string; ageBand: '9-11' | '12-15' | '16-17' | '18+'; rank: number; xp: number };
  locale: Locale;
  settings: PlayerSettings;
  progress: Record<string, { stars: number; score: number }>;
  endlessScores: Record<string, number>;
  resources: { hearts: number; credits: number; astrals: number; premium: boolean; heartUpdatedAt: number };
}

const STORAGE_KEY = 'singuloria-player-v2';
const randomCode = () => `SG-${Math.random().toString(36).slice(2, 6).toUpperCase()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`;

export const defaultPlayerState = (): PlayerState => ({
  version: 2,
  profile: { callsign: 'Nova', publicCode: randomCode(), avatar: 'MS', ageBand: '18+', rank: 1, xp: 0 },
  locale: 'de',
  settings: { masterVolume: 80, musicVolume: 65, effectsVolume: 85, reducedMotion: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false, fastAnimations: false, highContrast: false },
  progress: {},
  endlessScores: {},
  resources: { hearts: 5, credits: 1250, astrals: 40, premium: false, heartUpdatedAt: Date.now() },
});

export function loadPlayerState(): PlayerState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultPlayerState();
    const parsed = JSON.parse(raw) as PlayerState;
    if (parsed.version !== 2) return defaultPlayerState();
    const elapsedHearts = Math.floor((Date.now() - parsed.resources.heartUpdatedAt) / 1_800_000);
    if (elapsedHearts > 0 && parsed.resources.hearts < 5) {
      parsed.resources.hearts = Math.min(5, parsed.resources.hearts + elapsedHearts);
      parsed.resources.heartUpdatedAt += elapsedHearts * 1_800_000;
    }
    return parsed;
  } catch {
    return defaultPlayerState();
  }
}

export function savePlayerState(state: PlayerState): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export function exportPlayerState(state: PlayerState): void {
  const blob = new Blob([JSON.stringify(state, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `singuloria-${state.profile.callsign.toLowerCase()}-spielstand.json`;
  anchor.click();
  URL.revokeObjectURL(url);
}
